import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';

import { environment } from './environments/environment';
import { enableProdMode } from '@angular/core';

/**
 * Réglages qu'un déploiement a le droit de redéfinir à l'exécution.
 *
 * La liste est volontairement courte. `production` et la licence Syncfusion
 * sont des propriétés du **build**, pas du serveur qui l'héberge : un
 * `config.json` mal recopié qui porterait `production: false` désactiverait
 * les optimisations d'Angular sans que rien ne le signale.
 */
const OVERRIDABLE = ['BASE_URL', 'keycloak'] as const;

/**
 * Charge `/config.json` et applique à `environment` les seules clés autorisées.
 *
 * C'est ce qui permet à **une seule image** de servir plusieurs pays : chaque
 * conteneur monte son fichier
 * (`-v /opt/s2e/config.sa.json:/usr/share/nginx/html/config.json:ro`) et n'a
 * besoin ni d'un build ni d'un tag qui lui soit propre. Voir
 * `docs/MULTI-PAYS.md` §1 dans le workspace.
 *
 * Le fichier **n'est pas versionné** : absent, on garde les valeurs compilées
 * dans `environment*.ts`. C'est le cas de `ng serve` comme du conteneur
 * tunisien, qui continuent de fonctionner sans rien monter.
 *
 * Deux garde-fous, et le second n'est pas théorique : `nginx.conf` sert la SPA
 * par `try_files $uri $uri/ /index.html`, donc un fichier absent revient en
 * **200 avec le HTML de l'index**, pas en 404. Sans le contrôle du
 * `Content-Type`, `JSON.parse` lèverait à chaque démarrage sur l'instance qui
 * ne monte rien — c'est-à-dire la production tunisienne. Ne pas « simplifier »
 * ce contrôle.
 *
 * Surcharger `environment` après coup est sûr ici, et ça a été vérifié : les
 * 46 lectures de `BASE_URL` sont toutes des champs de classe
 * (`private readonly base = \`${environment.BASE_URL}/...\``), évalués à la
 * construction du service, donc après ce chargement. Une lecture au niveau
 * module — `const URL = \`${environment.BASE_URL}/x\`` — serait figée à
 * l'import et échapperait à la surcharge : ne pas en introduire.
 */
async function loadRuntimeConfig(): Promise<void> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) return;
    if (!response.headers.get('content-type')?.includes('json')) return;

    const config: Record<string, unknown> = await response.json();
    const target = environment as unknown as Record<string, unknown>;

    for (const key of OVERRIDABLE) {
      if (config[key] !== undefined) target[key] = config[key];
    }
  } catch {
    // Réseau coupé, JSON malformé, fichier tronqué : on démarre sur les
    // valeurs compilées plutôt que de refuser de démarrer.
  }
}

loadRuntimeConfig().then(() => {
  if (environment.production) {
    enableProdMode();
  }

  bootstrapApplication(AppComponent, appConfig).catch((err) =>
    // Un rejet d'initialiseur arrive souvent sans message : sans préfixe, la
    // console n'affiche qu'un « undefined » impossible à rattacher à sa cause.
    console.error("[bootstrap] L'application n'a pas pu démarrer :", err),
  );
});
